/* ==========================================================================
   FOOTBALL-THEME :: Football header behaviour
   - <sticky-header>: scroll state (`is-scrolled` / `is-hidden`) for the
     on-scroll-up and always modes. Skipped when data-sticky-type="none".
   - Desktop mega menus: hover + click + focus, 120ms close grace, Escape.
   - Mobile drawer: burger toggle, accordion, overlay / close button.
   Vanilla JS, no dependencies. Re-initialises on shopify:section:load.
   ========================================================================== */
(function () {
  'use strict';

  var DESKTOP = '(min-width: 990px)';
  var CLOSE_DELAY = 320;

  function closeMegas(header) {
    if (!header) return;

    header.querySelectorAll('.fs-header__item.is-open').forEach(function (item) {
      item.classList.remove('is-open');
      var link = item.querySelector('.fs-header__link');
      if (link) link.setAttribute('aria-expanded', 'false');
    });

    header.querySelectorAll('.fs-mega.is-open').forEach(function (mega) {
      mega.classList.remove('is-open');
    });

    header.classList.remove('is-open');
  }

  function initNav(header) {
    if (!header || header.dataset.fsNavReady === 'true') return;
    header.dataset.fsNavReady = 'true';

    var mql = window.matchMedia(DESKTOP);
    var closeTimer = null;
    var items = Array.prototype.slice.call(header.querySelectorAll('.fs-header__item'));

    function megaFor(item) {
      return header.querySelector('.fs-mega[data-index="' + item.getAttribute('data-index') + '"]');
    }

    function open(item) {
      if (!mql.matches) return;
      closeMegas(header);

      var mega = megaFor(item);
      item.classList.add('is-open');

      var link = item.querySelector('.fs-header__link');
      if (link) link.setAttribute('aria-expanded', 'true');

      if (mega) mega.classList.add('is-open');
      header.classList.add('is-open');
    }

    function close(item) {
      item.classList.remove('is-open');

      var link = item.querySelector('.fs-header__link');
      if (link) link.setAttribute('aria-expanded', 'false');

      var mega = megaFor(item);
      if (mega) mega.classList.remove('is-open');

      if (!header.querySelector('.fs-header__item.is-open')) header.classList.remove('is-open');
    }

    function cancelClose() {
      window.clearTimeout(closeTimer);
      closeTimer = null;
    }

    function delayClose(item) {
      cancelClose();
      closeTimer = window.setTimeout(function () {
        close(item);
        closeTimer = null;
      }, CLOSE_DELAY);
    }

    items.forEach(function (item) {
      var link = item.querySelector('.fs-header__link');
      var mega = megaFor(item);
      var hasPanel = !!mega;

      // Keep the panel open while the pointer travels into it (and while it
      // sits on the panel itself), so moving down to a link never closes it.
      if (mega) {
        mega.addEventListener('mouseenter', function () {
          if (!mql.matches) return;
          cancelClose();
        });

        mega.addEventListener('mouseleave', function () {
          if (!mql.matches) return;
          delayClose(item);
        });
      }

      item.addEventListener('mouseenter', function () {
        if (!mql.matches || !hasPanel) return;
        cancelClose();
        open(item);
      });

      item.addEventListener('mouseleave', function () {
        if (!mql.matches || !hasPanel) return;
        delayClose(item);
      });

      if (!link) return;

      link.addEventListener('click', function (event) {
        if (!mql.matches || !hasPanel) return;
        event.preventDefault();
        cancelClose();

        if (item.classList.contains('is-open')) {
          close(item);
        } else {
          open(item);
        }
      });

      link.addEventListener('focus', function () {
        if (!mql.matches || !hasPanel) return;
        cancelClose();
        open(item);
      });

      item.addEventListener('focusout', function (event) {
        if (!mql.matches || !hasPanel) return;
        var mega = megaFor(item);
        if (item.contains(event.relatedTarget)) return;
        if (mega && mega.contains(event.relatedTarget)) return;
        delayClose(item);
      });
    });

    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', function (event) {
        if (!event.matches) closeMegas(header);
      });
    }
  }

  function initDrawer(header, host) {
    if (!header || header.dataset.fsDrawerReady === 'true') return;
    header.dataset.fsDrawerReady = 'true';

    var scope = host || header.parentNode;
    var drawer = scope ? scope.querySelector('.fs-drawer') : null;
    var burger = header.querySelector('.fs-header__burger');
    if (!drawer || !burger) return;

    function closeAccordions() {
      drawer.querySelectorAll('.fs-drawer__toggle[aria-expanded]').forEach(function (btn) {
        btn.setAttribute('aria-expanded', 'false');
        btn.classList.remove('is-open');
        var panel = drawer.querySelector('#' + btn.getAttribute('aria-controls'));
        if (panel) panel.hidden = true;
      });
    }

    function setOpen(isOpen) {
      drawer.classList.toggle('is-open', isOpen);
      header.classList.toggle('is-open', isOpen);
      burger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      document.body.classList.toggle('overflow-hidden', isOpen);

      if (!isOpen) closeAccordions();
    }

    burger.addEventListener('click', function () {
      setOpen(!drawer.classList.contains('is-open'));
    });

    drawer.querySelectorAll('[data-fs-drawer-close]').forEach(function (el) {
      el.addEventListener('click', function () {
        setOpen(false);
      });
    });

    drawer.querySelectorAll('.fs-drawer__toggle[aria-controls]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var panel = drawer.querySelector('#' + btn.getAttribute('aria-controls'));
        var isOpen = btn.getAttribute('aria-expanded') === 'true';

        btn.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
        btn.classList.toggle('is-open', !isOpen);
        if (panel) panel.hidden = isOpen;
      });
    });

    drawer.fsClose = function () {
      setOpen(false);
    };
  }

  if (!customElements.get('sticky-header')) {
    customElements.define(
      'sticky-header',
      class FsStickyHeader extends HTMLElement {
        connectedCallback() {
          if (this.dataset.fsStickyReady === 'true') return;
          this.dataset.fsStickyReady = 'true';

          this.header = this.querySelector('.fs-header');
          this.stickyType = this.getAttribute('data-sticky-type') || 'on-scroll-up';
          this.lastY = window.pageYOffset || document.documentElement.scrollTop;
          this.ticking = false;

          this.onScroll = this.onScroll.bind(this);

          if (this.stickyType !== 'none') {
            window.addEventListener('scroll', this.onScroll, { passive: true });
            this.onScroll();
          }

          if (this.header) {
            initNav(this.header);
            initDrawer(this.header, this);
          }
        }

        disconnectedCallback() {
          window.removeEventListener('scroll', this.onScroll);
        }

        onScroll() {
          if (this.ticking) return;
          this.ticking = true;

          var self = this;
          window.requestAnimationFrame(function () {
            self.update();
            self.ticking = false;
          });
        }

        update() {
          var y = window.pageYOffset || document.documentElement.scrollTop;
          var height = this.offsetHeight || 64;

          this.classList.toggle('is-scrolled', y > 10);

          if (y > this.lastY && y > height * 2) {
            this.classList.add('is-hidden');
            if (this.header) closeMegas(this.header);
          } else if (y < this.lastY) {
            this.classList.remove('is-hidden');
          }

          this.lastY = y;
        }
      }
    );
  }

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;

    document.querySelectorAll('.fs-header').forEach(function (header) {
      closeMegas(header);
    });

    document.querySelectorAll('.fs-drawer').forEach(function (drawer) {
      if (typeof drawer.fsClose === 'function') drawer.fsClose();
    });
  });

  /* ------------------------------------------------------------------------
     GRAB THE SHEET: swipe the mobile drawer down to dismiss.
     Same physics as the filter dock: follows the finger, momentum decides,
     rubber-bands above the open position, and a running settle can be
     caught mid-flight. Only engages when the sheet content is scrolled to
     the top, so reading a long menu still scrolls normally. Needs
     assets/football-physics.js. */
  var sheetSprings = new WeakMap();
  var sheetGesture = null;

  function swallowSheetClick() {
    var handler = function (event) {
      cleanup();
      event.preventDefault();
      event.stopPropagation();
    };
    function cleanup() {
      window.clearTimeout(timer);
      document.removeEventListener('click', handler, true);
    }
    var timer = window.setTimeout(cleanup, 500);
    document.addEventListener('click', handler, true);
  }

  document.addEventListener('pointerdown', function (event) {
    if (!window.fsPhysics) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    var panel = event.target.closest('.fs-drawer__panel');
    if (!panel) return;
    var drawer = panel.closest('.fs-drawer');
    if (!drawer || !drawer.classList.contains('is-open')) return;
    if (event.target.closest('a, button, input, select, textarea')) return;

    var spring = sheetSprings.get(panel);
    if (spring) spring.stop();

    sheetGesture = {
      panel: panel,
      drawer: drawer,
      overlay: panel.parentElement
        ? panel.parentElement.querySelector('.fs-drawer__overlay')
        : null,
      startX: event.clientX,
      startY: event.clientY,
      startOffset: window.fsPhysics.translate(panel, 'y'),
      height: panel.offsetHeight || 1,
      y: 0,
      engaged: false,
      tracker: window.fsPhysics.tracker()
    };
    sheetGesture.tracker.add(event.clientY, performance.now());
  });

  window.addEventListener(
    'pointermove',
    function (event) {
      var g = sheetGesture;
      if (!g) return;

      if (!g.engaged) {
        var mx = event.clientX - g.startX;
        var my = event.clientY - g.startY;
        if (Math.abs(mx) < 8 && Math.abs(my) < 8) return;
        if (Math.abs(mx) > Math.abs(my) || my < 0) {
          sheetGesture = null; /* horizontal or upward: not a dismiss */
          return;
        }
        if (g.panel.scrollTop > 2) {
          sheetGesture = null; /* the sheet content scrolls */
          return;
        }
        g.engaged = true;
        g.panel.classList.add('is-dragging');
        g.panel.style.transition = 'none';
        g.startOffset = window.fsPhysics.translate(g.panel, 'y');
        g.startX = event.clientX;
        g.startY = event.clientY;
        g.tracker.reset();
      }

      g.tracker.add(event.clientY, performance.now());

      var dy = event.clientY - g.startY;
      var y = g.startOffset + dy;
      if (y < 0) y = -window.fsPhysics.rubberband(-y, g.height);

      g.y = y;
      g.panel.style.transform = 'translateY(' + y.toFixed(2) + 'px)';
      if (g.overlay) {
        g.overlay.style.transition = 'none';
        g.overlay.style.opacity = String(window.fsPhysics.clamp(1 - y / g.height, 0, 1));
      }
    },
    { passive: true }
  );

  function endSheetGesture() {
    var g = sheetGesture;
    if (!g) return;
    sheetGesture = null;
    if (!g.engaged) return;

    g.panel.classList.remove('is-dragging');
    var velocity = g.tracker.velocity(performance.now());
    var projected = g.y + window.fsPhysics.project(velocity);
    /* Velocity sign decides reverse vs commit; position only when parked. */
    var close;
    if (velocity > 250) {
      close = true; /* flicked toward dismiss */
    } else if (velocity < -250) {
      close = false; /* moving back toward open */
    } else {
      close = projected > g.height * 0.5; /* parked: judge by where it would rest */
    }

    swallowSheetClick();

    var finish = function (closed) {
      if (closed && typeof g.drawer.fsClose === 'function') g.drawer.fsClose();
      /* Hold the off-screen position while the class flips, then hand the
         transform back to CSS so the next open still animates. */
      g.panel.style.transition = 'none';
      g.panel.style.transform = '';
      if (g.overlay) g.overlay.style.opacity = '';
      void g.panel.offsetWidth;
      g.panel.style.transition = '';
      if (g.overlay) g.overlay.style.transition = '';
    };

    if (window.fsPhysics.reduced()) {
      finish(close);
      return;
    }

    var spring = sheetSprings.get(g.panel);
    if (!spring) {
      spring = window.fsPhysics.spring({
        from: g.y,
        to: g.y,
        epsilon: 0.2,
        onUpdate: function (value) {
          g.panel.style.transform = 'translateY(' + value.toFixed(2) + 'px)';
          if (g.overlay) {
            g.overlay.style.opacity = String(
              window.fsPhysics.clamp(1 - value / g.height, 0, 1)
            );
          }
        }
      });
      sheetSprings.set(g.panel, spring);
    } else {
      spring.jump(g.y);
    }

    spring.retarget(
      close ? g.height : 0,
      velocity,
      close ? 0.9 : 1,
      close ? 0.35 : 0.45,
      function () {
        finish(close);
      }
    );
  }

  window.addEventListener('pointerup', endSheetGesture);
  window.addEventListener('pointercancel', endSheetGesture);

  function initAll(root) {
    (root || document).querySelectorAll('.fs-header').forEach(function (header) {
      var host = header.closest('sticky-header');
      initNav(header);
      initDrawer(header, host);
    });
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }
})();
